import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Empty } from "@/components/atoms";

function Thread({ booking, onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/dm/${booking.id}`); setMessages(data.messages); } catch (e) {}
  }, [booking.id]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    try { await api.post(`/dm/${booking.id}`, { text: t }); load(); } catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]" data-testid="dm-thread">
      <div className="px-4 py-2 border-b-2 border-black flex items-center gap-3 bg-[#EAE4D3]">
        <button onClick={onBack} data-testid="dm-back"><ChevronLeft size={22} /></button>
        <div className="min-w-0">
          <p className="font-display font-bold leading-none truncate">{booking.tattoo_idea}</p>
          <p className="font-mono2 text-[10px] text-[#554E40]">Booking with {booking.requester_name}</p>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar p-4 flex flex-col gap-3">
        {messages.length === 0 && <p className="text-center text-sm text-[#554E40] mt-8">No messages yet.</p>}
        {messages.map((m) => {
          const mine = m.user_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.id}`}>
              <div className={`max-w-[78%] border-2 border-black rounded-lg px-3 py-2 ${m.is_admin ? "bg-[#EAE4D3]" : mine ? "bg-[#C7A656]" : "bg-[#F9F6F0]"}`}>
                <p className="font-display font-bold text-[10px] mb-1">{m.author_name}{m.is_admin ? " · Parlor" : ""}</p>
                <p className="text-sm break-words">{m.text}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t-2 border-black bg-[#EAE4D3]">
        <input className="atc-input flex-1" placeholder="Message..." value={text} onChange={(e) => setText(e.target.value)} data-testid="dm-input" />
        <button type="submit" className="atc-btn btn-primary px-4" data-testid="dm-send-btn"><Send size={18} /></button>
      </form>
    </div>
  );
}

export default function Messages() {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/dm/threads"); setThreads(data); } catch (e) { setThreads([]); }
  };
  useEffect(() => { load(); }, []);

  if (active) return <Thread booking={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div data-testid="messages-page">
      <div className="px-5 pt-5">
        <span className="eyebrow">Private Line</span>
        <h1 className="font-display font-black text-3xl uppercase leading-tight">Messages</h1>
      </div>
      {threads === null ? <Loading /> : threads.length === 0 ? <Empty title="No conversations" sub="Booking DMs from the parlor show up here." /> : (
        <div className="px-5 py-4 flex flex-col gap-3">
          {threads.map((t) => (
            <button key={t.booking.id} className="atc-card p-4 text-left" onClick={() => setActive(t.booking)} data-testid={`dm-thread-${t.booking.id}`}>
              <p className="font-display font-bold leading-tight">{t.booking.tattoo_idea}</p>
              <p className="font-mono2 text-[10px] text-[#554E40] mt-1">with {t.booking.requester_name}</p>
              {t.last_message && <p className="text-sm text-[#554E40] mt-1 truncate">{t.last_message.author_name}: {t.last_message.text}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
